import React, { useCallback, useEffect, useRef } from "react";
import { Box, Typography, Avatar, IconButton, styled } from "@mui/material";
import { CloudUpload, DeleteOutline } from "@mui/icons-material";
import { Controller, useWatch } from "react-hook-form";
import { Stack } from "@mui/system";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import { rules } from "../utils";

const styles = {
  mandatoryAsterisk: {
    color: "red",
  },
  descriptionText: {
    color: "grey",
  },
  hintText: {
    color: "red",
  },
  deleteButton: {
    backgroundColor: "#36bfba",
    "&:hover": {
      backgroundColor: "#dcf1f1",
    },
  },
  container: {
    marginBottom: "18px",
  },
  uploadBox: {
    width: 150,
    height: 150,
    border: "2px dashed",
    borderColor: "divider",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    overflow: "hidden",
    position: "relative",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "primary.main",
    },
  },
  uploadBoxError: {
    width: 150,
    height: 150,
    border: "2px dashed",
    borderColor: "error.main",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    overflow: "hidden",
    position: "relative",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "error.main",
    },
  },
  avatar: {
    width: "100%",
    height: "100%",
    borderRadius: 2,
  },
  uploadText: {
    mt: 1,
    color: "text.secondary",
  },
  uploadTextError: {
    mt: 1,
    color: "error.main",
  },
  helperMessageContainer: {
    flexDirection: "row",
  },
};

// Replace with your own base64 placeholder or external image URL
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Centralized validation config to support future field types
const DEFAULT_VALIDATION = {
  allowedMimeTypes: ["image/jpeg", "image/png"],
  maxSizeMB: 2,
  checkDimensions: true,
  minWidth: 250,
  minHeight: 250,
  maxWidth: 1024,
  maxHeight: 1024,
  hintText: undefined,
};

const VALIDATION_CONFIG = {
  logo: {
    // Uses DEFAULT_VALIDATION (2MB + dimensions)
    hintText: "*** Use a square image to prevent distortion. ***",
  },
  merchantLogo: {
    maxSizeMB: 1,
    checkDimensions: false,
  },
};

const ImageUploader = ({
  name,
  control,
  setError,
  clearErrors,
  isEdit,
  description,
  isLoading,
  previewUrl,
  setPreviewUrl,
  mandatory,
  merchantId,
}) => {
  const isUserInteracted = useRef(false);
  const handleFileChange = useCallback(
    (onChange, file) => {
      if (!file) {
        onChange(null);
        setPreviewUrl("");
        return;
      }

      const config = {
        ...DEFAULT_VALIDATION,
        ...(VALIDATION_CONFIG[name] || {}),
      };

      // type checking
      if (!config.allowedMimeTypes.includes(file.type)) {
        setError(name, {
          type: "manual",
          message: "You can only upload JPG/PNG file!",
        });
        return;
      }

      // size checking
      const maxSizeInBytes = config.maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        setError(name, {
          type: "manual",
          message: `Image must smaller than ${config.maxSizeMB} MB!`,
        });
        return;
      }

      const processFile = () => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          const rawBase64String = reader?.result?.split(",")[1];
          // base64 string type checking
          if (
            !(
              rawBase64String?.startsWith("iVBORw0KGgo") ||
              rawBase64String?.startsWith("/9j/")
            )
          ) {
            setError(name, {
              type: "manual",
              message: "You can only upload JPG/PNG file!",
            });
            return;
          }
          isUserInteracted.current = false;
          onChange(base64String); // Store base64 string in form
          setPreviewUrl(base64String);
        };
        reader.readAsDataURL(file);
        clearErrors(name);
      };

      // Skip dimensions check when disabled by config
      if (!config.checkDimensions) {
        processFile();
        return;
      }

      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        // image dimensions checking
        if (
          width < config.minWidth ||
          height < config.minHeight ||
          width > config.maxWidth ||
          height > config.maxHeight
        ) {
          setError(name, {
            type: "manual",
            message: `Image dimensions must be between ${config.minWidth}x${config.minHeight}px and ${config.maxWidth}x${config.maxHeight}px`,
          });
          URL.revokeObjectURL(img.src);
          return;
        }
        URL.revokeObjectURL(img.src);
        processFile();
      };
      img.src = URL.createObjectURL(file);
    },
    [setError, clearErrors, name],
  );
  const currentValue = useWatch({ name, control });
  const fieldConfig = {
    ...DEFAULT_VALIDATION,
    ...(VALIDATION_CONFIG[name] || {}),
  };

  // Render the api data (if exist)
  useEffect(() => {
    if (currentValue) {
      console.log(`Applying ${name} API image`);
      setPreviewUrl(currentValue);
    } else {
      // Clear previewUrl when form value is cleared
      setPreviewUrl("");
    }
  }, [currentValue, name, setPreviewUrl]);

  return (
    <Stack sx={styles.container}>
      <Typography variant="content">
        Merchant logo
        {mandatory && <span style={styles.mandatoryAsterisk}> *</span>}{" "}
        <span style={styles.descriptionText}>{description}</span>
      </Typography>
      {fieldConfig.hintText && (
        <Typography variant="content" sx={styles.hintText}>
          {fieldConfig.hintText}
        </Typography>
      )}
      <Controller
        name={name}
        control={control}
        rules={rules({ required: mandatory })}
        render={({ field, fieldState: { error } }) => (
          <Box sx={{ textAlign: "center", position: "relative" }}>
            <Stack gap={2} direction={"row"} alignItems={"center"}>
              <Box sx={{ position: "relative" }}>
                <VisuallyHiddenInput
                  accept="image/png, image/jpeg"
                  id={`${name}-upload-${merchantId || 'default'}`}
                  type="file"
                  onChange={(e) =>
                    handleFileChange(field.onChange, e.target.files?.[0])
                  }
                  disabled={!isEdit || previewUrl || isLoading}
                />
                <label htmlFor={`${name}-upload-${merchantId || 'default'}`}>
                  <Box sx={error ? styles.uploadBoxError : styles.uploadBox}>
                    {previewUrl ? (
                      <Avatar
                        key={previewUrl}
                        src={previewUrl}
                        variant="square"
                        sx={styles.avatar}
                      />
                    ) : (
                      <>
                        <CloudUpload
                          fontSize="large"
                          color={error ? "error" : "action"}
                        />
                        <Typography
                          variant="body2"
                          sx={
                            error ? styles.uploadTextError : styles.uploadText
                          }
                        >
                          Click to upload
                        </Typography>
                      </>
                    )}
                  </Box>
                </label>
              </Box>

              {previewUrl && isEdit && (
                // Delete button - only show in edit mode
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    isUserInteracted.current = true; // Mark user interaction
                    field.onChange(null); // Clear form value
                    setPreviewUrl(""); // Clear preview (no default image)
                    clearErrors(name);
                  }}
                  sx={styles.deleteButton}
                  disabled={isLoading}
                >
                  <DeleteOutline sx={{ color: "white" }} fontSize="medium" />
                </IconButton>
              )}
            </Stack>
            <Stack sx={styles.helperMessageContainer}>
              {error && <HelperMessage message={error.message} />}
            </Stack>
          </Box>
        )}
      />
    </Stack>
  );
};

export default ImageUploader;
